/**
 * Schema Tab Content - displays table schema/fields
 *
 * Data Flow:
 * - Receives table details from PreviewSidePanel (parent) as a prop
 * - Parent has already fetched the data, avoiding duplicate API calls
 * - If refetch is needed, use queryClient.invalidateQueries in parent
 */

import { useMemo } from 'react';
import {
  Skeleton,
  Table as ShadcnTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@loader/ui';
import type { TableApiResponse } from '@loader/shared-types';

import type { FieldInfo } from './types';

import { getTypeIcon } from '@/features/schemas/constants/fieldTypes';

interface SchemaTabProps {
  tableDetails: TableApiResponse | undefined;
  isLoading?: boolean;
}

export function SchemaTab({ tableDetails, isLoading = false }: SchemaTabProps) {
  const fields = useMemo((): FieldInfo[] => {
    if (!tableDetails) return [];
    return (tableDetails.fields || []).map((f) => ({
      id: f.id,
      name: f.name,
      displayName: f.name,
      dataType: f.data_type,
      isRequired: f.is_required,
      isUnique: f.is_unique,
      description: f.description,
    }));
  }, [tableDetails]);

  if (isLoading) {
    return (
      <div className="space-y-2 py-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (fields.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        No fields defined
      </div>
    );
  }

  return (
    <div className="py-2">
      <ShadcnTable>
        <TableHeader>
          <TableRow>
            <TableHead>Field</TableHead>
            <TableHead className="w-16 text-center">Req</TableHead>
            <TableHead className="w-16 text-center">Unique</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field) => {
            const TypeIcon = getTypeIcon(field.dataType);
            const fieldContent = (
              <div className="flex items-center gap-2">
                <TypeIcon className="text-muted-foreground size-4 shrink-0" />
                <div className="flex min-w-0 flex-1 items-center gap-1">
                  <span className="truncate font-medium">{field.displayName}</span>
                  {field.displayName !== field.name && (
                    <span className="text-muted-foreground shrink-0 text-xs">({field.name})</span>
                  )}
                </div>
              </div>
            );

            return (
              <TableRow key={field.id}>
                <TableCell className="max-w-0">
                  {fieldContent}
                </TableCell>
                <TableCell className="text-center">
                  {field.isRequired ? '✓' : '–'}
                </TableCell>
                <TableCell className="text-center">
                  {field.isUnique ? '✓' : '–'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </ShadcnTable>
    </div>
  );
}
